// Seeker-facing in-session therapy booking.
// Flow: pick tier (G3/G4/G5) -> request -> counsellor accepts -> pay ->
// a 'to_be_scheduled' appointment is created.
//   GET  /api/care-grades
//   POST /api/pricing/in-session-booking/request
//   GET  /api/pricing/in-session-booking/status/{id}
//   POST /api/pricing/in-session-booking/payment-link
//   POST /api/pricing/in-session-booking/verify-payment
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { CalendarPlus, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function InSessionBooking({ sessionId, user, onBooked, defaultOpen = false }) {
  const [tiers, setTiers] = useState([]);
  const [open, setOpen] = useState(defaultOpen);
  const [phase, setPhase] = useState('idle'); // idle | requesting | awaiting_approval | awaiting_payment | booked | failed
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/care-grades`)
      .then((res) => setTiers((res.data || []).filter((g) => g.grade_type === 'session' && g.tier)))
      .catch(() => setTiers([]));
    if (document.getElementById('razorpay-checkout-js')) return;
    const s = document.createElement('script');
    s.id = 'razorpay-checkout-js';
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const proceedToPayment = async (bk) => {
    try {
      const linkRes = await axios.post(`${API}/pricing/in-session-booking/payment-link`, null,
        { params: { booking_id: bk.id, seeker_user_id: user.id } });
      setPhase('awaiting_payment');
      openRazorpay(bk.id, linkRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start payment');
      setPhase('failed');
    }
  };

  const requestTier = async (grade) => {
    setError(null);
    setPhase('requesting');
    try {
      const res = await axios.post(`${API}/pricing/in-session-booking/request`,
        { session_id: sessionId, grade_code: grade.code },
        { params: { user_id: user.id, user_role: user.role } });
      const bk = res.data.booking;
      setBooking(bk);
      if (bk.status === 'approved' || bk.status === 'payment_pending') {
        await proceedToPayment(bk);
      } else {
        setPhase('awaiting_approval');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not request booking');
      setPhase('failed');
    }
  };

  // Poll for counsellor approval
  useEffect(() => {
    if (phase !== 'awaiting_approval' || !booking) return undefined;
    const iv = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/pricing/in-session-booking/status/${booking.id}`);
        const st = res.data.status;
        if (st === 'approved' || st === 'payment_pending') {
          clearInterval(iv);
          await proceedToPayment(res.data);
        } else if (['rejected', 'expired'].includes(st)) {
          clearInterval(iv);
          setError('The counsellor did not approve the booking.');
          setPhase('failed');
        }
      } catch (_) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, booking]);

  const openRazorpay = (bookingId, order) => {
    if (!window.Razorpay) {
      toast.error('Payment SDK not loaded — please retry');
      setPhase('failed');
      return;
    }
    const rzp = new window.Razorpay({
      key: order.razorpay_key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.razorpay_order_id,
      name: 'Veyonika',
      description: `Book ${order.grade_name || order.grade_code}`,
      prefill: order.prefill || {},
      notes: order.notes || {},
      handler: async (response) => {
        try {
          const v = await axios.post(`${API}/pricing/in-session-booking/verify-payment`, null, {
            params: {
              booking_id: bookingId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
          });
          setPhase('booked');
          toast.success('Therapy session booked — to be scheduled');
          onBooked?.(v.data);
        } catch (err) {
          setError(err.response?.data?.detail || 'Payment verification failed');
          setPhase('failed');
        }
      },
      modal: { ondismiss: () => setPhase('idle') },
      theme: { color: '#7C3AED' },
    });
    rzp.open();
  };

  if (phase === 'booked') {
    return (
      <div className="card p-4 bg-emerald-50 border-emerald-200" data-testid="booking-confirmed">
        <div className="flex items-center gap-3">
          <CheckCircle className="text-emerald-600" size={22} />
          <p className="text-sm text-emerald-800">Therapy session booked. Your counsellor will assign a date & time.</p>
        </div>
      </div>
    );
  }

  if (phase === 'awaiting_approval') {
    return (
      <div className="card p-4 bg-amber-50 border-amber-200" data-testid="booking-awaiting-approval">
        <div className="flex items-center gap-3">
          <Loader2 className="animate-spin text-amber-600" size={22} />
          <div>
            <p className="text-sm font-medium text-amber-800">Waiting for counsellor to accept</p>
            <p className="text-sm text-amber-700">Payment opens automatically once accepted.</p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'requesting' || phase === 'awaiting_payment') {
    return (
      <div className="card p-4 bg-purple-50 border-purple-200" data-testid="booking-pending">
        <div className="flex items-center gap-3">
          <Loader2 className="animate-spin text-purple-600" size={22} />
          <p className="text-sm text-purple-800">
            {phase === 'requesting' ? 'Sending request…' : 'Complete payment in the Razorpay window'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4" data-testid="in-session-booking">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 font-medium text-[#2D3748]"
        data-testid="book-therapy-toggle"
      >
        <CalendarPlus size={20} className="text-primary" />
        Book a Therapy Session
      </button>
      {phase === 'failed' && (
        <div className="flex items-center gap-2 mt-2 text-sm text-red-700" data-testid="booking-failed">
          <XCircle size={16} /> {error || 'Please try again.'}
        </div>
      )}
      {open && (
        <div className="mt-3 space-y-2" data-testid="tier-options">
          <p className="text-sm text-[#718096]">Choose a tier. Your counsellor will accept, then you can pay.</p>
          {tiers.map((g) => (
            <button
              key={g.code}
              onClick={() => requestTier(g)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors"
              data-testid={`book-tier-${g.code}`}
            >
              <span className="text-sm font-medium text-[#2D3748]">{g.name}</span>
              <span className="text-sm text-primary font-semibold">₹{g.discount_price || g.base_price}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
